import React, { useState, useEffect, useRef, ChangeEvent, DragEvent, DragEventHandler } from 'react';
import Uppy from '@uppy/core';
import AwsS3Multipart from '@uppy/aws-s3-multipart';
import { UppyFile } from '@uppy/core'; // Import UppyFile type
import { MediaKitSettingModel } from './KitModels';
import { showToast } from '../../shared/Toast';

interface CustomUppyUploaderProps {
  backendUrl: string; // Base URL for your backend S3 pre-signing endpoint
  mediaSetting: MediaKitSettingModel;
  handleCloseUploader: () => void;
  filesUploaded: (file: any) => void;
}

interface UploadedFileState {
  progress: number; // 0-100
  status: 'pending' | 'uploading' | 'success' | 'error' | 'canceled';
  uploadURL?: string; // S3 URL after successful upload
  errorMessage?: string;

  file?: UppyFile; // The actual File object
  previewUrl: string; // The URL created by URL.createObjectURL
}

const CustomUppyUploader: React.FC<CustomUppyUploaderProps> = ({
  backendUrl,
  mediaSetting,
  handleCloseUploader,
  filesUploaded,
}) => {
  const uppy = useRef<Uppy | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isDraggingOver, setIsDraggingOver] = useState(false);

  const [uploadedFileStates, setUploadedFileStates] = useState<UploadedFileState[]>([]);
  const [totalProgress, setTotalProgress] = useState<number>(0); // Overall upload progress
  const [isUploading, setIsUploading] = useState<boolean>(false);
  const [uploadComplete, setUploadComplete] = useState<boolean>(false);

  useEffect(() => {
    if (!uppy.current) {
      let allowedTypes = [];
      ;
      if (mediaSetting.imageAllowed > 0) {
        allowedTypes.push('image/*');
      }
      if (mediaSetting.videoAllowed > 0) {
        allowedTypes.push('video/*');
      }
      uppy.current = new Uppy({
          debug: true, // Set to false in production
          autoProceed: false, // We'll manually trigger upload
          restrictions: {
            maxNumberOfFiles: 5,
            maxFileSize: 100 * 1024 * 1024, // 100MB (adjust as needed)
            allowedFileTypes: allowedTypes,
          },
        });

      // Add the AWS S3 Multipart plugin
      uppy.current.use(AwsS3Multipart, {
        companionUrl: '/', // Uppy will append /s3/multipart or /s3/params
        // serverUrl: backendUrl, // Ensure this matches Companion's base URL if you're using it
        // get-url: `${backendUrl}/s3-multipart`, // Specify your custom endpoint for multipart signing if not using companion's default routes.
        // It's crucial that this backend endpoint correctly implements the S3 multipart protocol.
        // If you are using `@uppy/aws-s3` (single-part), the endpoint would be simpler like `${backendUrl}/s3-sign-url`.
      });

      // --- Uppy Event Listeners ---
      uppy.current.on('file-added', (file: UppyFile) => {
        // Add file to our state for display
        let previewUrl = '';
        if (file.data && (file.type?.startsWith('image/') || file.type?.startsWith('video/'))) {
          previewUrl = URL.createObjectURL(file.data);
        }
        if (file.type?.startsWith('image/')) {
          const resp = uploadedFileStates.filter((fileItem) => {
            return fileItem.file.type?.startsWith('image/');
          });
          if(resp && (resp.length+1) > mediaSetting.imageAllowed)
          {
            return;
          }
        }
        if (file.type?.startsWith('video/')) {
          const resp = uploadedFileStates.filter((fileItem) => {
            return fileItem.file.type?.startsWith('cideo/');
          });
          if (resp && resp.length + 1 > mediaSetting.videoAllowed) {
            return;
          }
        }
          setUploadedFileStates((prevFiles) => [
            ...prevFiles,
            {
              previewUrl: previewUrl,
              file: file,
              progress: 0,
              status: 'pending',
            },
          ]);
        setUploadComplete(false); // Reset completion status on new files
      });

      uppy.current.on('file-removed', (file: UppyFile) => {
        setUploadedFileStates((prevFiles) => {
          const fileToRemove = prevFiles.find((f) => f.file.id === file.id);
          if (fileToRemove?.previewUrl) {
            URL.revokeObjectURL(fileToRemove.previewUrl); // Clean up the Blob URL
          }
          return prevFiles.filter((f) => f.file.id !== file.id);
        });
      });

      uppy.current.on(
        'upload-progress',
        (file: UppyFile, progress: { bytesUploaded: number; bytesTotal: number }) => {
          // Update individual file's progress
          setUploadedFileStates((prevFiles) =>
            prevFiles.map((f) =>
              f.file.id === file.id
                ? {
                    ...f,
                    progress: Math.round((progress.bytesUploaded / progress.bytesTotal) * 100),
                    status: 'uploading',
                  }
                : f
            )
          );
        }
      );

      uppy.current.on('progress', (totalProgress: number) => {
        // Update overall progress
        setTotalProgress(totalProgress);
      });

      uppy.current.on('upload-success', async (file: UppyFile, response: any) => {
        // Mark file as success and store its URL
        await filesUploaded(file);
        setUploadedFileStates((prevFiles) =>
          prevFiles.map((f) =>
            f.file.id === file.id
              ? { ...f, status: 'success', uploadURL: response.uploadURL || response.body.url }
              : f
          )
        );
      });

      uppy.current.on('complete', (result) => {
        setIsUploading(false); // Upload process finished
        if (result.failed.length === 0) {
          setUploadComplete(true); // All files uploaded successfully
          console.log('Upload successful!', result);
        } else {
          console.error('Upload failed for some files:', result.failed);
          setUploadedFileStates((prevFiles) =>
            prevFiles.map((f) => {
              if (result.failed.some((failedFile) => failedFile.id === f.file.id)) {
                return { ...f, status: 'error', errorMessage: 'Upload failed' }; // Basic error message
              }
              return f;
            })
          );
        }
      });

      uppy.current.on('error', (error) => {
        console.error('Uppy error:', error);
        setIsUploading(false);
        alert(`Uppy Error: ${error.message}`);
      });
    }

    // Cleanup Uppy instance on component unmount
    return () => {
      uploadedFileStates.forEach((file) => {
        // Make sure to clean up any remaining preview URLs
        console.log('cleanup previewurl');
        if (file.previewUrl) {
          URL.revokeObjectURL(file.previewUrl);
        }
      });
      if (uppy.current) {
        console.log('cleanup uppy.current');
        uppy.current.close();
        uppy.current = null; // Important to prevent memory leaks
      }
    };
  }, [backendUrl]); // Re-initialize if backendUrl changes (unlikely for a single app)

  const handleUpload = () => {
    if (uppy.current && uploadedFileStates.length > 0) {
      setIsUploading(true);
      setUploadComplete(false);
      uppy.current.upload(); // Start the upload process
    } else {
      alert('Please select files to upload.');
    }
  };

  const handleClearFiles = () => {
    if (uppy.current) {
      uppy.current.getFiles().forEach((file) => {
        uppy.current?.removeFile(file.id);
      });
      setUploadedFileStates([]);
      setTotalProgress(0);
      setIsUploading(false);
      setUploadComplete(false);
    }
  };
  const handleClose = () => {
    handleClearFiles();
    handleCloseUploader();
  };
  const handleDragOver = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault(); // This is crucial to allow the onDrop event to fire
    setIsDraggingOver(true);
  };

  const handleDragLeave = (e: DragEvent<HTMLDivElement>) => {
    setIsDraggingOver(false);
  };

  const handleDrop = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDraggingOver(false);
    const droppedFiles = Array.from(e.dataTransfer.files);
    console.log('Dropped files:', droppedFiles);
    addInUppyList(droppedFiles);
  };

  const addInUppyList = (droppedFiles:any) => {
    let currentImage = 1;
    let currentVideo = 1;
    let reachMaxed = false;
    Array.from(droppedFiles).forEach((file:any) => {
      const present = uppy.current.getFiles().find((fileAdded) => {
        return fileAdded.name == file.name;
      });
      if (present) {
        return;
      }
      if (file.type?.startsWith('image/')) {
        const resp = uploadedFileStates.filter((fileItem) => {
          return fileItem.file.type?.startsWith('image/');
        });
        if (
          currentImage > mediaSetting.imageAllowed || resp &&
          (resp.length + currentImage) > mediaSetting.imageAllowed
        ) {
          reachMaxed = true;
          return;
        }
        currentImage++;
      }
      if (file.type?.startsWith('video/')) {
        const resp = uploadedFileStates.filter((fileItem) => {
          return fileItem.file.type?.startsWith('cideo/');
        });
        if (resp && resp.length + currentVideo > mediaSetting.videoAllowed) {
          reachMaxed = true;
          return;
        }
        currentVideo++;
      }
      
      uppy.current?.addFile({
        data: file,
        name: file.name,
        type: file.type,
        size: file.size,
      });
    });
    if (reachMaxed) {
      showToast(
        'Reached max limit.',
        'danger'
      );
    }
  };

  const handleFileChange = (event: ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && uppy.current) {
      addInUppyList(event.target.files);

      // Array.from(event.target.files).forEach((file) => {
      //   uppy.current?.addFile({
      //     data: file,
      //     name: file.name,
      //     type: file.type,
      //     size: file.size,
      //   });
      // });
      event.target.value = '';
    }
  };
  return (
    <div className="p-8 border rounded shadow-lg bg-white w-full mx-auto">
      <h2 className="font-semibold text-2xl leading-8 text-gray-900 mb-2 sm:mb-4">
        Upload media files
      </h2>
      <hr className="mb-2 sm:mb-4" />
      {/* Hidden file input */}
      <input
        type="file"
        multiple
        ref={fileInputRef}
        onChange={handleFileChange}
        style={{ display: 'none' }}
        accept="image/*,video/*,.pdf,.doc,.docx" // Use the same accept as Uppy restrictions for consistency
      />

      <div className="sm:gap-4 sm:items-start h-full bg-white outline-none">
        <div className="mt-1 sm:mt-0 sm:col-span-2 h-full">
          <div className="uppy-Root" dir="ltr">
            <div
              onClick={() => fileInputRef.current?.click()}
              // type="button"
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
              className={`
                w-full h-48 border-2 border-dashed rounded-lg p-4
                flex items-center justify-center text-center transition-all duration-200
                ${isDraggingOver ? 'border-blue-500 bg-blue-50' : 'border-gray-300 bg-gray-50'}
              `}
            >
              <div className="uppy-DragDrop-inner">
                <svg
                  aria-hidden="true"
                  focusable="false"
                  className="uppy-c-icon uppy-DragDrop-arrow"
                  width="16"
                  height="16"
                  viewBox="0 0 16 16"
                >
                  <path d="M11 10V0H5v10H2l6 6 6-6h-3zm0 0" fill-rule="evenodd"></path>
                </svg>
                <div className="uppy-DragDrop-label">
                  <span className="uppy-DragDrop-browse">Upload a file</span> or drag and drop
                </div>
                <p className="text-lg text-gray-500">
                  {isDraggingOver ? 'Drop your files here!' : ''}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* <button
        onClick={() => fileInputRef.current?.click()}
        className="w-full bg-blue-500 text-white py-2 px-4 rounded-md hover:bg-blue-600 transition-colors mb-4"
        disabled={isUploading}
      >
        Select Files
      </button> */}

      {/* Display selected files */}
      {uploadedFileStates.length > 0 && (
        <div className="mb-4">
          <h3 className="text-lg font-semibold mb-2">Selected Files:</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
            {uploadedFileStates.map((uploadedFileState) => (
              <div
                key={uploadedFileState.file.id}
                className="border border-gray-200 rounded-lg overflow-hidden shadow-sm flex flex-col"
              >
                <div className="flex-grow flex items-start justify-center p-2 bg-gray-100">
                  {uploadedFileState.file.type.startsWith('image') ? (
                    <img
                      draggable={false}
                      src={uploadedFileState.previewUrl}
                      alt={uploadedFileState.file.name}
                      className="max-w-full max-h-48 object-contain rounded-md"
                    />
                  ) : uploadedFileState.file.type.startsWith('video') ? (
                    <video
                      draggable={false}
                      src={uploadedFileState.previewUrl}
                      controls
                      className="max-w-full max-h-48 object-contain rounded-md"
                    >
                      Your browser does not support the video tag.
                    </video>
                  ) : (
                    <div className="text-center text-gray-500 py-8">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="h-12 w-12 mx-auto text-gray-400"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0014.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z"
                        />
                      </svg>
                      <p className="mt-2 text-sm">{uploadedFileState.file.name}</p>
                      <p className="text-xs text-gray-400">({uploadedFileState.file.type})</p>
                    </div>
                  )}
                </div>
                <div className="p-3 bg-white border-t border-gray-200">
                  <span className="font-medium">{uploadedFileState.file.name}</span>{' '}
                  <span className="text-gray-500">({uploadedFileState.progress}%)</span> -{' '}
                  {uploadedFileState.status === 'pending' && (
                    <span className="text-gray-500">Pending</span>
                  )}
                  {uploadedFileState.status === 'uploading' && (
                    <span className="text-blue-500">{uploadedFileState.progress === 100 ? 'Processing...' : 'Uploading...'}</span>
                  )}
                  {uploadedFileState.status === 'success' && (
                    <span className="text-green-600">
                      Uploaded!{' '}
                      {uploadedFileState.uploadURL && (
                        <a
                          href={uploadedFileState.uploadURL}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-blue-600 hover:underline ml-1"
                        >
                          View
                        </a>
                      )}
                    </span>
                  )}
                  {uploadedFileState.status === 'error' && (
                    <span className="text-red-600">
                      Error: {uploadedFileState.errorMessage || 'Unknown error'}
                    </span>
                  )}
                  {uploadedFileState.status === 'canceled' && (
                    <span className="text-orange-500">Canceled</span>
                  )}
                  <p className="text-xs text-gray-500">
                    {(uploadedFileState.file.data.size / 1024 / 1024).toFixed(2)} MB
                  </p>
                  <button
                    onClick={() => {
                      console.log('remove file');
                      uppy.current?.removeFile(uploadedFileState.file.id);
                    }} // Use Uppy's removeFile, which triggers our listener
                    className="mt-1 text-red-600 hover:text-red-800 text-sm font-medium focus:outline-none"
                    disabled={isUploading}
                  >
                    Remove
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Overall Progress Bar */}
      {uploadedFileStates.length > 0 && (totalProgress > 0 || isUploading) && (
        <div className="mb-4">
          <h3 className="text-lg font-semibold mb-2">Overall Progress:</h3>
          <div className="w-full bg-gray-200 rounded-full h-4">
            <div
              className="bg-green-500 h-4 rounded-full text-xs font-medium text-white text-center p-0.5 leading-none transition-all duration-300"
              style={{ width: `${totalProgress}%` }}
            >
              {totalProgress}%
            </div>
          </div>
        </div>
      )}

      {/* Action Buttons */}
      <div className="flex space-x-4 mt-6">
        <button
          onClick={handleUpload}
          className={`flex-1 py-2 px-4 rounded-md font-semibold transition-colors
            ${
              uploadedFileStates.length > 0 && !isUploading
                ? 'bg-green-500 text-white hover:bg-green-600'
                : 'bg-gray-400 text-gray-700 cursor-not-allowed'
            }`}
          disabled={!uploadedFileStates.length || isUploading}
        >
          {isUploading ? (totalProgress === 100 ? 'Processing...' : 'Uploading...') : 'Start Upload'}
        </button>
        <button
          onClick={handleClearFiles}
          className={`flex-1 py-2 px-4 rounded-md font-semibold transition-colors
            ${
              uploadedFileStates.length > 0 && !isUploading
                ? 'bg-red-500 text-white hover:bg-red-600'
                : 'bg-gray-400 text-gray-700 cursor-not-allowed'
            }`}
          disabled={!uploadedFileStates.length || isUploading}
        >
          Clear Files
        </button>
        <button
          onClick={handleClose}
          className={`flex-1 py-2 px-4 rounded-md font-semibold transition-colors
            ${
              !isUploading
                ? 'items-center px-4 py-2 border border-gray-300 shadow-sm text-base font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500'
                : 'bg-gray-400 text-gray-700 cursor-not-allowed'
            }`}
          disabled={isUploading}
        >
          Close
        </button>
      </div>
      {uploadComplete && (
        <p className="text-green-600 mt-4 text-center font-semibold">
          All files uploaded successfully!
        </p>
      )}
    </div>
  );
};

export default CustomUppyUploader;
